import { brandAndProductOf, brandPathOf } from '@atlas/core-utils/classification'
import { AxisCode } from '@atlas/types'
import { type ChipProps, cn } from '@atlas/ui-primitives'
import { useQuery } from '@tanstack/react-query'
import { combinedComplexityCode, isTerminalStatus, statusLabel, type WorkOrder } from '@/domain/work-order.types'
import { useAuth } from '@/hooks/useAuth'
import { useBootstrapValues } from '@/hooks/useClassification'
import { resolveTupleLabel, useAxesWithValues } from '@/hooks/useLibrary'
import { useMyReviews } from '@/hooks/useMyReviews'
import { useWorkOrderBoard } from '@/hooks/useWorkOrders'
import { humanizeCode } from '@/lib/labels'
import { activityApi, type EventDTO } from '@/services/activity.service'
import { HomeHero } from './HomeHero'
import { type MyWorkOrderRow, MyWorkOrdersCard } from './MyWorkOrdersCard'
import { RecentActivityCard } from './RecentActivityCard'
import { ReviewQueueCard } from './ReviewQueueCard'

const greetingFor = (hour: number): string =>
	hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening'

const statusVariantFor = (status: string): ChipProps['variant'] => {
	if (status === 'completed') return 'success'
	if (status === 'in_progress') return 'primary'
	if (status === 'backlog') return 'muted'
	return 'secondary'
}

const statusDotFor = (status: string): string => {
	if (status === 'completed') return 'bg-ds-success'
	if (status === 'in_progress') return 'bg-ds-info'
	if (status === 'backlog') return 'bg-ds-border-strong'
	if (status.includes('validation') || status.includes('review')) return 'bg-ds-warning'
	return 'bg-ds-text-subtle'
}

/** `09:12` today, `Yst` yesterday, `Mon` this week, a short date beyond. */
const shortTime = (iso: string): string => {
	const then = new Date(iso)
	const now = new Date()
	const days = Math.floor((now.setHours(0, 0, 0, 0) - new Date(then).setHours(0, 0, 0, 0)) / 86_400_000)
	if (days <= 0) return then.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
	if (days === 1) return 'Yst'
	if (days < 7) return then.toLocaleDateString([], { weekday: 'short' })
	return then.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

const eventText = (event: EventDTO): string => {
	const [entity, action] = event.type.split('.')
	const subject = typeof event.data.orderCode === 'string' ? event.data.orderCode : humanizeCode(entity ?? '')
	return `${subject} — ${humanizeCode(action ?? event.type)}`
}

/** The review card lists the longest waits; the rest are counted, not listed. */
const REVIEW_PREVIEW_ROWS = 3

const isOverdue = (workOrder: WorkOrder): boolean =>
	!!workOrder.dueAt && new Date(workOrder.dueAt).getTime() < Date.now()

export function DashboardPage() {
	const { user } = useAuth()
	const board = useWorkOrderBoard()
	const { axes } = useAxesWithValues()
	const brandValues = useBootstrapValues(AxisCode.BRAND)
	const reviews = useMyReviews(REVIEW_PREVIEW_ROWS)
	const activity = useQuery({
		queryKey: ['activity', 'recent'],
		queryFn: () => activityApi.listRecent(8),
	})
	const recentEvents = activity.data?.data ?? []

	const firstName = (user?.displayName || user?.email || 'there').split(/[\s@]/)[0]
	const orders = board.data?.workOrders ?? []
	const workflows = board.data?.workflows ?? []
	const myOpen = orders
		.filter((w) => (user?.userId ? w.assigneeIds.includes(user.userId) : false))
		.filter((w) => !isTerminalStatus(w, workflows))
	const overdue = myOpen.filter(isOverdue)

	const toRow = (workOrder: WorkOrder): MyWorkOrderRow => {
		// The order's `brand` tag holds the PRODUCT when one was chosen (brand and product
		// are two levels of one axis), so the row meta resolves the brand root through the
		// brand path rather than the raw tag label — see WorkOrderDetailsPanel's
		// useBrandAndProduct.
		const { brandCode } = brandAndProductOf(brandPathOf(brandValues, workOrder.tuple[AxisCode.BRAND]?.[0]))
		const brand = brandCode ? (brandValues.find((v) => v.code === brandCode)?.label ?? brandCode) : null
		const market = resolveTupleLabel(axes, AxisCode.GEOGRAPHY, workOrder.tuple)
		return {
			workOrderId: workOrder.workOrderId,
			orderCode: workOrder.orderCode,
			title: workOrder.title,
			meta: [brand, market].filter(Boolean).join(' · ') || (combinedComplexityCode(workOrder) ?? ''),
			statusLabel: statusLabel(workOrder.status),
			statusVariant: statusVariantFor(workOrder.status),
			dotClass: statusDotFor(workOrder.status),
		}
	}

	const todayCount = recentEvents.filter((e) => shortTime(e.timestamp).includes(':')).length

	return (
		<div>
			<HomeHero
				greeting={greetingFor(new Date().getHours())}
				firstName={firstName}
				stats={[
					{ value: String(myOpen.length), label: 'My open orders', to: '/work-orders' },
					...(reviews.canReview
						? [{ value: String(reviews.total), label: 'Awaiting my review', to: '/work-orders' }]
						: []),
					{ value: String(overdue.length), label: 'Overdue — at risk', to: '/work-orders', alert: overdue.length > 0 },
				]}
			/>

			<div
				className={cn('grid grid-cols-1 items-start gap-3.5', reviews.canReview ? 'xl:grid-cols-3' : 'xl:grid-cols-2')}
			>
				{reviews.canReview ? (
					<ReviewQueueCard rows={reviews.rows} total={reviews.total} isLoading={reviews.isLoading} />
				) : null}
				<MyWorkOrdersCard rows={myOpen.slice(0, 6).map(toRow)} isLoading={board.isLoading} />
				<RecentActivityCard
					rows={recentEvents.map((event) => ({
						id: event.eventId,
						time: shortTime(event.timestamp),
						text: eventText(event),
					}))}
					isLoading={activity.isLoading}
					pulse={
						todayCount > 0
							? `${todayCount} event${todayCount === 1 ? '' : 's'} across the factory today — every one classified and audited at the sink.`
							: 'A quiet morning — no factory events yet today.'
					}
				/>
			</div>
		</div>
	)
}
